import { WA_PHYSICS } from './constants';

export type UpdateCallback = (deltaTime: number) => void;
export type RenderCallback = (interpolation: number) => void;

export class GameLoop {
  private lastTime = 0;
  private accumulator = 0;
  private running = false;
  private animationFrameId: number | null = null;
  private backgroundIntervalId: number | null = null;
  private updateFn: UpdateCallback;
  private renderFn: RenderCallback;

  constructor(updateFn: UpdateCallback, renderFn: RenderCallback) {
    this.updateFn = updateFn;
    this.renderFn = renderFn;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibility);
      this.handleVisibility();
    } else {
      this.loop(this.lastTime);
    }
  }

  stop(): void {
    this.running = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.backgroundIntervalId !== null) {
      clearInterval(this.backgroundIntervalId);
      this.backgroundIntervalId = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibility);
    }
  }

  /**
   * requestAnimationFrame stops in background tabs, which would freeze the
   * game (snapshots pile up, turn/camera triggers are missed). When hidden,
   * keep ticking on a (browser-throttled) interval instead.
   */
  private handleVisibility = (): void => {
    if (!this.running || typeof document === 'undefined') return;
    if (document.hidden) {
      if (this.backgroundIntervalId === null) {
        this.backgroundIntervalId = window.setInterval(() => this.loop(performance.now()), 200);
      }
    } else {
      if (this.backgroundIntervalId !== null) {
        clearInterval(this.backgroundIntervalId);
        this.backgroundIntervalId = null;
      }
      // Resume the rAF-driven loop without a catch-up burst or a duplicate
      // frame chain.
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
      }
      this.lastTime = performance.now();
      this.animationFrameId = requestAnimationFrame(this.loop);
    }
  };

  private loop = (currentTime: number): void => {
    if (!this.running) return;

    const frameTime = Math.min(currentTime - this.lastTime, 250);
    this.lastTime = currentTime;
    this.accumulator += frameTime;

    while (this.accumulator >= WA_PHYSICS.TICK_MS) {
      this.updateFn(WA_PHYSICS.TICK_MS / 1000);
      this.accumulator -= WA_PHYSICS.TICK_MS;
    }

    const interpolation = this.accumulator / WA_PHYSICS.TICK_MS;
    this.renderFn(interpolation);

    if (typeof document === 'undefined' || !document.hidden) {
      this.animationFrameId = requestAnimationFrame(this.loop);
    }
  };

  isRunning(): boolean {
    return this.running;
  }
}
